import { useLocation, useNavigate } from "react-router-dom";
import { useGettingStarted, type TourTrack } from "./GettingStartedContext";
import TourSpotlight from "./TourSpotlight";
import TourPopup from "./TourPopup";
import ExampleReferralModal from "./ExampleReferralModal";

const isDashboardPath = (path: string) =>
  path === "/" || path === "/dashboard" || path === "/organization";

interface StepConfig {
  message: string;
  targetSelector?: string;
  popupPreferredPlacement?: "auto" | "top" | "bottom";
  showGotIt: boolean;
  showBackLink: boolean;
  helperText?: string;
}

/**
 * Per-track step content. `dashboard` runs while the user is still on the
 * dashboard (spotlights the nav/header element they need to tap); `target`
 * runs once they've arrived at the destination page (spotlights the element
 * that teaches the next action).
 */
const TRACK_CONFIG: Record<
  Exclude<TourTrack, "example">,
  { targetPath: string; dashboard: StepConfig; target: StepConfig }
> = {
  partners: {
    targetPath: "/network",
    dashboard: {
      message: "Tap Network to find trade partners.",
      targetSelector: '[data-tour-target="nav-network"]',
      popupPreferredPlacement: "top",
      showGotIt: false,
      showBackLink: true,
      helperText: "Optional walkthrough",
    },
    target: {
      message:
        "Tap Request to connect. Accepted connections appear in My Partners.",
      targetSelector: '[data-tour-target="demo-partner-request"]',
      popupPreferredPlacement: "auto",
      showGotIt: true,
      showBackLink: false,
    },
  },
  invite: {
    targetPath: "/qr",
    dashboard: {
      message: "Tap here to invite a trade partner.",
      targetSelector: '[data-tour-target="header-qr"]',
      popupPreferredPlacement: "bottom",
      showGotIt: false,
      showBackLink: true,
      helperText: "Optional walkthrough",
    },
    target: {
      message:
        "Tap Share, then choose Messages to text your invite link from your own number.",
      targetSelector: '[data-tour-target="qr-share"]',
      popupPreferredPlacement: "top",
      showGotIt: true,
      showBackLink: false,
    },
  },
  referral: {
    targetPath: "/send-referral",
    dashboard: {
      message: "Tap + to start a new referral.",
      targetSelector: '[data-tour-target="nav-plus"]',
      popupPreferredPlacement: "top",
      showGotIt: false,
      showBackLink: true,
      helperText: "Optional walkthrough",
    },
    target: {
      message:
        "Add your customer's information, describe the job, choose a partner, and review.",
      // No selector — form is large; centered popup with full dim.
      popupPreferredPlacement: "auto",
      showGotIt: true,
      showBackLink: false,
    },
  },
};

export default function GettingStartedTour() {
  const { activeTrack, endTrack } = useGettingStarted();
  const location = useLocation();
  const navigate = useNavigate();

  if (!activeTrack) return null;

  // Track 4 is a single modal, not a spotlight tour.
  if (activeTrack === "example") {
    return <ExampleReferralModal isOpen={true} onClose={endTrack} />;
  }

  const config = TRACK_CONFIG[activeTrack];
  const path = location.pathname;

  let step: StepConfig | null = null;
  if (isDashboardPath(path)) {
    step = config.dashboard;
  } else if (path.startsWith(config.targetPath)) {
    step = config.target;
  }

  if (!step) {
    // User is on an unrelated page (e.g. Settings). Hide the spotlight but
    // keep the track active so it reappears on return to a relevant page.
    return null;
  }

  const handleReturn = () => {
    endTrack();
    navigate("/dashboard");
  };

  return (
    <TourSpotlight
      targetSelector={step.targetSelector}
      popupPreferredPlacement={step.popupPreferredPlacement}
    >
      <TourPopup
        message={step.message}
        helperText={step.helperText}
        showGotIt={step.showGotIt}
        showBackLink={step.showBackLink}
        onGotIt={handleReturn}
        onExit={endTrack}
        onBack={handleReturn}
      />
    </TourSpotlight>
  );
}
